import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import "./styles/ContactUs.css";
import "../styles/global.css";
import {MapPinPen,Phone,Mail,ShoppingCart, Undo2,FileQuestionMark,BadgeIndianRupee,Info} from "lucide-react";



/* ─── Responsive hook ─── */
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const fn = () => setW(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return { isMobile: w < 576, isTablet: w >= 576 && w < 992, isDesktop: w >= 992 };
}

/* ─────────────────────────────────────────
   Base styles — same palette as AboutUs
───────────────────────────────────────── */
const S = {
  hero: {
    position: "relative",
    overflow: "hidden",
    background: "#2F4A3A",
    color: "#fff",
  },
  heroBubble: (size, top, left, opacity) => ({
    position: "absolute",
    width: size,
    height: size,
    borderRadius: "50%",
    background: "rgba(255,255,255," + opacity + ")",
    top,
    left,
    pointerEvents: "none",
  }),
  heroTag: {
    display: "inline-block",
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.35)",
    borderRadius: "50px",
    padding: "6px 20px",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "2px",
    textTransform: "uppercase",
    marginBottom: "18px",
    color: "#fff",
  },
  heroTitle: {
    fontSize: "clamp(1.8rem, 3vw, 3.6rem)",
    fontWeight: 500,
    lineHeight: 1.15,
    marginBottom: "20px",
    // fontFamily: "'Playfair Display', Georgia, serif",
  },
  heroSub: {
    fontSize: "1rem",
    opacity: 0.88,
    lineHeight: 1.7,
  },
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    background: "rgba(255,255,255,0.18)",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: "12px",
    padding: "10px 16px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#fff",
  },

  sectionTitle: {
    fontSize: "clamp(1.5rem, 3.5vw, 2.6rem)",
    fontWeight: 800,
    color: "#0f172a",
    marginBottom: "12px",
  },
  accent: { color: "#58A186" },
  divider: {
    width: "56px", height: "4px", borderRadius: "4px",
    background: "linear-gradient(90deg, #58A186, #C38635)",
    margin: "0 0 24px",
  },
  dividerCenter: {
    width: "56px", height: "4px", borderRadius: "4px",
    background: "linear-gradient(90deg, #58A186, #C38635)",
    margin: "0 auto 24px",
  },
  subText: { color: "#64748b", fontSize: "1rem", lineHeight: 1.75 },

  infoCard: (bg) => ({
    background: bg,
    borderRadius: "20px",
    padding: "32px 28px",
    height: "100%",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 4px 24px rgba(12,25,41,0.08)",
    color: "#fff",
  }),
  infoIcon: {
    width: "52px", height: "52px", borderRadius: "14px",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "22px", marginBottom: "16px",
    background: "rgba(255,255,255,0.25)",
  },
  infoTitle: {
    // fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: "1.15rem", fontWeight: 500, marginBottom: "8px", color: "#fff",
  },
  infoText: { color: "rgba(255,255,255,0.88)", lineHeight: 1.75, fontSize: "0.95rem" },
  infoLink: { color: "#fff", fontWeight: 600, textDecoration: "none", fontSize: "0.95rem" },

  formCard: {
    background: "#fff", borderRadius: "20px",
    boxShadow: "0 4px 32px rgba(12,25,41,0.09)",
    border: "1px solid #eef1f6",
  },
  formLabel: {
    fontWeight: 600, fontSize: "0.88rem", color: "#0f172a",
    marginBottom: "6px", display: "block", letterSpacing: "0.3px",
  },
  formInput: {
    width: "100%", padding: "12px 16px", borderRadius: "12px",
    border: "1.5px solid #e2e8f0", fontSize: "0.95rem", color: "#0f172a",
    outline: "none", transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    fontFamily: "inherit", background: "#f8fafc", boxSizing: "border-box",
  },
  formInputFocus: {
    borderColor: "#1E7A8C",
    boxShadow: "0 0 0 3px rgba(30,122,140,0.1)",
    background: "#fff",
  },
  formTextarea: {
    width: "100%", padding: "12px 16px", borderRadius: "12px",
    border: "1.5px solid #e2e8f0", fontSize: "0.95rem", color: "#0f172a",
    outline: "none", transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    fontFamily: "inherit", background: "#f8fafc", resize: "vertical",
    minHeight: "130px", boxSizing: "border-box",
  },
  submitBtn: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    gap: "10px",
    background: "#2F4A3A",
    color: "#fff", fontWeight: 700, fontSize: "15px",
    padding: "14px 36px", borderRadius: "50px", border: "none",
    cursor: "pointer", transition: "transform 0.2s ease, box-shadow 0.2s ease",
    boxShadow: "0 4px 16px rgba(30,122,140,0.3)", width: "100%",
  },

  faqCard: {
    background: "#fff", borderRadius: "16px", padding: "24px 20px",
    boxShadow: "0 2px 16px rgba(12,25,41,0.06)", border: "1px solid #eef1f6",
    transition: "transform 0.22s ease", height: "100%",
  },
  faqIcon: (bg) => ({
    width: "48px", height: "48px", borderRadius: "12px", background: bg,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "20px", marginBottom: "14px",
  }),
  faqTitle: { fontWeight: 700, fontSize: "0.97rem", color: "#0f172a", marginBottom: "7px" },
  faqText: { color: "#64748b", fontSize: "0.88rem", lineHeight: 1.65 },

  ctaStrip: {
    background: "linear-gradient(135deg, #1E7A8C 0%, #167A7A 50%, #238495 100%)",
    borderRadius: "20px", color: "#fff",
    position: "relative", overflow: "hidden",
  },
  ctaTitle: {
    // fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: "clamp(1.4rem, 2vw, 2.2rem)", fontWeight: 500, marginBottom: "12px",
  },
  ctaBtn: {
    display: "inline-flex", alignItems: "center", gap: "8px",
    background: "#fff", color: "#1E7A8C", fontWeight: 700, fontSize: "15px",
    padding: "13px 28px", borderRadius: "50px", border: "none",
    cursor: "pointer", textDecoration: "none",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
  },
  ctaBtnOutline: {
    display: "inline-flex", alignItems: "center", gap: "8px",
    background: "transparent", color: "#fff", fontWeight: 700, fontSize: "15px",
    padding: "12px 28px", borderRadius: "50px",
    border: "2px solid rgba(255,255,255,0.6)",
    cursor: "pointer", textDecoration: "none", transition: "all 0.2s ease",
  },
};

const contactInfo = [
  {
  icon: <MapPinPen style={{ fill: "transparent" }} />,
  bg: "linear-gradient(145deg, #1E7A8C, #238495)",
  title: "Visit Us",
  lines: [
    "No: 3/195, Mount Poonamallee Rd, opposite MIOT Hospital, Parthasarathy Nagar, Manapakkam, Chennai, Tamil Nadu 600089",
  ],
  link: null,
},
  {
    icon: <Phone />,
    bg: "linear-gradient(145deg, #C38635, #a06e28)",
    title: "Call Us",
    lines: ["Mon – Sat, 9 AM – 6 PM"],
    phone: "+91 9500946632",
    link: "tel:+919500946632",
    
  },
  {
    icon:  <Mail />,
    bg: "linear-gradient(145deg, #167A7A, #0d5858)",
    title: "Email Us",
    lines: ["We reply within 24 hours"],
    phone: "mainadmin@shreevarma.org",
    link: "mailto:mainadmin@shreevarma.org",
  },
];

const quickHelp = [
  { icon: <ShoppingCart />, bg: "rgba(88,161,134,0.12)", title: "Order Support", text: "Questions about your order, tracking, or delivery? Our team is ready to help." },
  { icon:  <Undo2 />, bg: "rgba(30,122,140,0.12)", title: "Returns & Refunds", text: "Easy hassle-free returns within 7 days. We'll guide you through the process." },
  { icon:  <FileQuestionMark />, bg: "rgba(195,134,53,0.12)", title: "Product Queries", text: "Not sure which product suits you? Our wellness advisors are just a message away." },
  { icon: <BadgeIndianRupee />, bg: "rgba(22,122,122,0.12)", title: "Wholesale / B2B", text: "Interested in bulk orders or partnership? Let's talk business." },
];

const subjectOptions = [
  "General Enquiry", "Order & Tracking", "Product Information",
  "Returns & Refunds", "Wholesale / B2B", "Feedback / Suggestions", "Other",
];

export default function ContactUs() {
  const { isMobile, isTablet } = useBreakpoint();
  const isSmall = isMobile || isTablet;

  const [hovered, setHovered] = useState(null);
  const [focused, setFocused] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/contactUs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok && data.status) {
        setSubmitted(true);
      } else {
        setSubmitError(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setSubmitError("Unable to send your message right now. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };
  const inputStyle = (f) => ({ ...S.formInput, ...(focused === f ? S.formInputFocus : {}) });
  const taStyle   = (f) => ({ ...S.formTextarea, ...(focused === f ? S.formInputFocus : {}) });

  /* ── responsive tokens ── */
  const heroPad   = isMobile ? "72px 0 52px" : isTablet ? "80px 0 60px" : "100px 0 80px";
  const secPad    = isMobile ? "52px 0"      : "80px 0";
  const greyPad   = isMobile ? "52px 0"      : "80px 0";
  const ctaPad    = isMobile ? "28px 24px"   : isTablet ? "40px 32px" : "60px 48px";
  const formPad   = isMobile ? "28px 20px"   : isTablet ? "36px 28px" : "44px 40px";

  return (
    <>
      <Header />
      {/* ── Breadcrumb ── */}
      <section style={{ padding: "14px 0", background: "#f8fafc", borderBottom: "1px solid #eef1f6" }}>
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0" style={{ fontSize: isMobile ? "0.82rem" : "0.9rem" }}>
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active" aria-current="page">Contact Us</li>
            </ol>
          </nav>
        </div>
      </section>

      {/* ── Hero ── */}
      <section style={{ ...S.hero, padding: heroPad }}>
        {!isMobile && <div style={S.heroBubble("320px", "-80px", "-80px", 0.06)}></div>}
        {!isMobile && <div style={S.heroBubble("200px", "40px", "60%", 0.05)}></div>}

        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div className="row align-items-center g-4 g-lg-5">

            {/* Left copy */}
            <div className="col-12 col-lg-6">
              <div style={S.heroTag}>💬 Get In Touch</div>
              <h1 style={S.heroTitle}>
                We'd Love to{isMobile ? " " : <br />}Hear From You
              </h1>
              <p style={{ ...S.heroSub, maxWidth: isMobile ? "100%" : "520px" }}>
                Whether you have a question about our products, need wellness guidance, or just want to say hello — our team is here for you every step of the way.
              </p>
              <div
                className="hero-badge-group"
                style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "20px" }}
              >
                <div style={S.heroBadge}><span>⏱️</span> Replies within 24 hours</div>
                <div style={S.heroBadge}><span>🌿</span> Expert wellness advice</div>
              </div>
            </div>

            {/* Right stat grid */}
            <div className="col-12 col-lg-6">
              <div
                className="hero-stat-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                }}
              >
              {[
  {
    icon: "fa-solid fa-phone",
    label: "Call Us",
    val: "+91 9500946632",
    href: "tel:+919500946632",
  },
  {
    icon: "fa-solid fa-envelope",
    label: "Email Us",
    val: "mainadmin@shreevarma.org",
    href: "mailto:mainadmin@shreevarma.org",
  },
  {
    icon: "fa-solid fa-clock",
    label: "Working Hours",
    val: "Mon–Sat, 9AM–6PM",
  },
  {
    icon: "fa-solid fa-location-dot",
    label: "Location",
    val: "Chennai, Tamil Nadu",
  },
].map((item, i) => (
  <div
    key={i}
    style={{
      background: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.22)",
      borderRadius: "16px",
      padding: isMobile ? "18px 14px" : "24px 18px",
      textAlign: "center",
      backdropFilter: "blur(8px)",
    }}
  >
    <div
      style={{
        fontSize: isMobile ? "1.5rem" : "1.8rem",
        marginBottom: "8px",
      }}
    >
      <i className={item.icon}></i>
    </div>

    <div
      style={{
        fontSize: "0.72rem",
        opacity: 0.75,
        fontWeight: 600,
        letterSpacing: "1px",
        textTransform: "uppercase",
        marginBottom: "4px",
      }}
    >
      {item.label}
    </div>

    {item.href ? (
      <a
        href={item.href}
        style={{
          fontSize: isMobile ? "0.78rem" : "0.86rem",
          fontWeight: 700,
          wordBreak: "break-all",
          color: "inherit",
          textDecoration: "none",
        }}
      >
        {item.val}
      </a>
    ) : (
      <div
        style={{
          fontSize: isMobile ? "0.78rem" : "0.86rem",
          fontWeight: 700,
          wordBreak: "break-all",
        }}
      >
        {item.val}
      </div>
    )}
  </div>
))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Ways to Reach Us ── */}
      <section style={{ padding: secPad }}>
        <div className="container">
          <div className="text-center mb-4 mb-md-5">
            <div style={S.dividerCenter}></div>
            <h2 style={{ ...S.sectionTitle, textAlign: "center" }}>Ways to <span style={S.accent}>Reach Us</span></h2>
            <p style={{ ...S.subText, maxWidth: "480px", margin: "0 auto" }}>
              Multiple channels, one goal — making sure you always get the help you need.
            </p>
          </div>
          <div className="row g-3 g-md-4 info-cards-row">
            {contactInfo.map((c, i) => (
              <div className="col-12 col-md-4" key={i}>
                <div style={S.infoCard(c.bg)}>
                  <div style={{ position: "absolute", width: "120px", height: "120px", borderRadius: "50%", background: "rgba(255,255,255,0.06)", top: "-30px", right: "-30px", pointerEvents: "none" }}></div>
                  <div style={S.infoIcon}>{c.icon}</div>
                  <div style={S.infoTitle}>{c.title}</div>
                  {c.lines.map((l, j) => <p key={j} style={{ ...S.infoText, marginBottom: "2px" }}>{l}</p>)}
                  {c.link && (
                    <a href={c.link} style={{ ...S.infoLink, marginTop: "10px", display: "inline-block" }}>
                      {c.phone} →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Form + Map ── */}
      <section style={{ background: "#f8fafc", padding: greyPad }}>
        <div className="container">
          <div className="row g-4 g-lg-5 align-items-start">

            {/* Form column */}
            <div className="col-12 col-lg-7">
              <div style={S.divider}></div>
              <h2 style={S.sectionTitle}>Send Us a <span style={S.accent}>Message</span></h2>
              <p style={{ ...S.subText, marginBottom: "28px" }}>
                Fill in the form below and we'll get back to you within one business day.
              </p>

              {submitted ? (
                <div style={{
                  background: "#2F4A3A",
                  border: "1.5px solid rgba(88,161,134,0.3)",
                  borderRadius: "20px",
                  padding: isMobile ? "36px 24px" : "48px 36px",
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: "3rem", marginBottom: "16px" }}>✅</div>
                  <h3 style={{fontWeight: 500, color: "#0f172a", marginBottom: "10px" }}>
                    Message Sent!
                  </h3>
                  <p style={S.subText}>Thank you for reaching out. Our team will get back to you within 24 hours.</p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }}
                    style={{ ...S.submitBtn, width: "auto", marginTop: "20px", padding: "12px 28px" }}
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <div className="contact-form" style={{ ...S.formCard, padding: formPad }}>
                  <form onSubmit={handleSubmit}>
                    {submitError && (
                      <div className="col-12" style={{
                        background: "rgba(231,76,60,0.08)",
                        border: "1px solid rgba(231,76,60,0.3)",
                        color: "#c0392b",
                        borderRadius: "10px",
                        padding: "10px 14px",
                        marginBottom: "16px",
                        fontSize: "0.88rem",
                      }}>
                        {submitError}
                      </div>
                    )}
                    <div className="row g-3">
                      <div className="col-12 col-sm-6">
                        <label style={S.formLabel}>Full Name <span style={{ color: "#e74c3c" }}>*</span></label>
                        <input
                          type="text" name="name" required placeholder="Enter Your Name"
                          value={form.name} onChange={handleChange}
                          onFocus={() => setFocused("name")} onBlur={() => setFocused(null)}
                          style={inputStyle("name")}
                        />
                      </div>
                      <div className="col-12 col-sm-6">
                        <label style={S.formLabel}>Email Address <span style={{ color: "#e74c3c" }}>*</span></label>
                        <input
                          type="email" name="email" required placeholder="Enter Your E-mail"
                          value={form.email} onChange={handleChange}
                          onFocus={() => setFocused("email")} onBlur={() => setFocused(null)}
                          style={inputStyle("email")}
                        />
                      </div>
                      <div className="col-12 col-sm-6">
                        <label style={S.formLabel}>Phone Number</label>
                        <input
                          type="tel" name="phone" placeholder="Enter Your Phone Number"
                          value={form.phone} onChange={handleChange}
                          onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)}
                          style={inputStyle("phone")}
                        />
                      </div>
                      <div className="col-12 col-sm-6">
                        <label style={S.formLabel}>Subject <span style={{ color: "#e74c3c" }}>*</span></label>
                        <select
                          name="subject" required value={form.subject} onChange={handleChange}
                          onFocus={() => setFocused("subject")} onBlur={() => setFocused(null)}
                          style={{ ...inputStyle("subject"), cursor: "pointer" }}
                        >
                          <option value="">Select a topic…</option>
                          {subjectOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                      <div className="col-12">
                        <label style={S.formLabel}>Your Message <span style={{ color: "#e74c3c" }}>*</span></label>
                        <textarea
                          name="message" required placeholder="Tell us how we can help you…"
                          value={form.message} onChange={handleChange}
                          onFocus={() => setFocused("message")} onBlur={() => setFocused(null)}
                          style={taStyle("message")}
                        />
                      </div>
                      <div className="col-12" style={{ marginTop: "4px" }}>
                        <button
                          type="submit"
                          disabled={submitting}
                          style={{ ...S.submitBtn, opacity: submitting ? 0.7 : 1, cursor: submitting ? "not-allowed" : "pointer" }}
                          onMouseEnter={e => { if (!submitting) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(30,122,140,0.4)"; } }}
                          onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(30,122,140,0.3)"; }}
                        >
                         {submitting ? "Sending..." : "Send Message"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>

            {/* Map + Hours column */}
            <div className="col-12 col-lg-5">
              <div style={S.divider}></div>
              <h2 style={S.sectionTitle}>Our <span style={S.accent}>Location</span></h2>
              <p style={{ ...S.subText, marginBottom: "20px" }}>
                
              </p>

              {/* Map */}
              <div style={{
                borderRadius: "18px", overflow: "hidden",
                boxShadow: "0 4px 24px rgba(12,25,41,0.10)",
                border: "1px solid #eef1f6", marginBottom: "20px",
              }}>
                <iframe
                  className="map-iframe"
                  title="Store Location"
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d18444.12377699659!2d80.184032!3d13.020654000000002!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5261c4c9b2695d%3A0x8b7b43f0a69879bb!2sShreevarma%20Bio%20Naturals%20Pvt.%20Ltd%20%7C%20Corporate%20Office%20%7C%20Shreevarma%20Ayurveda!5e1!3m2!1sen!2sin!4v1781690238086!5m2!1sen!2sin"
                  width="100%"
                  height={isMobile ? "200" : "260"}
                  style={{ border: 0, display: "block" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />



               
              </div>

              {/* Working hours */}
              <div style={{
                background: "#fff", borderRadius: "16px",
                padding: isMobile ? "18px 16px" : "22px 20px",
                boxShadow: "0 2px 16px rgba(12,25,41,0.06)",
                border: "1px solid #eef1f6",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                  <span style={{ fontSize: "20px" }}>🕐</span>
                  <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.97rem" }}>Working Hours</span>
                </div>
                {[
                  { day: "Monday – Saturday", time: "9:00 AM – 4:00 PM", open: true },
                  { day: "Sunday",          time: "Closed",            open: false },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="hours-row"
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      flexWrap: "wrap", gap: "6px",
                      padding: "10px 0",
                      borderBottom: i < 2 ? "1px solid #f1f5f9" : "none",
                    }}
                  >
                    <span style={{ color: "#64748b", fontSize: "0.88rem" }}>{row.day}</span>
                    <span style={{
                      fontSize: "0.84rem", fontWeight: 600,
                      color: row.open ? "#58A186" : "#e74c3c",
                      background: row.open ? "rgba(88,161,134,0.10)" : "rgba(231,76,60,0.08)",
                      padding: "3px 10px", borderRadius: "50px",
                      whiteSpace: "nowrap",
                    }}>{row.time}</span>
                  </div>
                ))}
              </div>

              {/* Quick contact strip — mobile-only shortcut */}
              {isMobile && (
                <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
                  <a
                    href="tel:+919876543210"
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      gap: "8px", background: "linear-gradient(135deg, #1E7A8C, #238495)",
                      color: "#fff", fontWeight: 700, fontSize: "14px",
                      padding: "13px 10px", borderRadius: "12px", textDecoration: "none",
                      boxShadow: "0 4px 14px rgba(30,122,140,0.3)",
                    }}
                  >
                    📞 Call Now
                  </a>
                  <a
                    href="mailto:support@yourstore.in"
                    style={{
                      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                      gap: "8px", background: "#fff",
                      color: "#1E7A8C", fontWeight: 700, fontSize: "14px",
                      padding: "13px 10px", borderRadius: "12px", textDecoration: "none",
                      border: "1.5px solid #1E7A8C",
                    }}
                  >
                    ✉️ Email Us
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Quick Help Cards ── */}
      <section style={{ padding: secPad }}>
        <div className="container">
          <div className="text-center mb-4 mb-md-5">
            <div style={S.dividerCenter}></div>
            <h2 style={{ ...S.sectionTitle, textAlign: "center" }}>How Can We <span style={S.accent}>Help?</span></h2>
            <p style={{ ...S.subText, maxWidth: "480px", margin: "0 auto" }}>
              Pick the category that best fits your query and we'll connect you to the right person.
            </p>
          </div>
          <div className="row g-3 g-md-4">
            {quickHelp.map((q, i) => (
              <div className="col-6 col-lg-3" key={i}>
                <div
                  style={{
                    ...S.faqCard,
                    padding: isMobile ? "20px 16px" : S.faqCard.padding,
                    transform: hovered === `q${i}` ? "translateY(-4px)" : "none",
                    boxShadow: hovered === `q${i}` ? "0 8px 28px rgba(12,25,41,0.12)" : S.faqCard.boxShadow,
                  }}
                  onMouseEnter={() => setHovered(`q${i}`)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div style={{ ...S.faqIcon(q.bg), width: isMobile ? "40px" : "48px", height: isMobile ? "40px" : "48px", fontSize: isMobile ? "18px" : "20px" }}>{q.icon}</div>
                  <div style={{ ...S.faqTitle, fontSize: isMobile ? "0.88rem" : "0.97rem" }}>{q.title}</div>
                  {!isMobile && <div style={S.faqText}>{q.text}</div>}
                  {isMobile && <div style={{ ...S.faqText, fontSize: "0.78rem" }}>{q.text}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Strip ── */}
      <section style={{ padding: isMobile ? "32px 0 48px" : "60px 0" }}>
        <div className="container">
          <div style={{ ...S.ctaStrip, padding: ctaPad }}>
            {!isMobile && (
              <>
                <div style={{ position: "absolute", width: "300px", height: "300px", borderRadius: "50%", background: "rgba(255,255,255,0.05)", right: "-60px", top: "-80px", pointerEvents: "none" }}></div>
                <div style={{ position: "absolute", width: "180px", height: "180px", borderRadius: "50%", background: "rgba(255,255,255,0.05)", right: "80px", bottom: "-60px", pointerEvents: "none" }}></div>
              </>
            )}
            <div className="row align-items-center g-3 g-lg-4" style={{ position: "relative", zIndex: 1 }}>
              <div className="col-12 col-lg-7">
                <div style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "2px", textTransform: "uppercase", opacity: 0.8, marginBottom: "10px" }}>
                  Explore Our Products
                </div>
                <h2 style={S.ctaTitle}>While You're Here, Discover Our Range</h2>
                <p style={{ opacity: 0.85, fontSize: "0.97rem", lineHeight: 1.7, marginBottom: isMobile ? "4px" : 0 }}>
                  Browse 500+ natural wellness products crafted with care and backed by science.
                </p>
              </div>
              <div
                className="col-12 col-lg-5 cta-btn-group"
                style={{
                  display: "flex", flexWrap: "wrap", gap: "12px",
                  justifyContent: isSmall ? "flex-start" : "flex-end",
                }}
              >
                <Link to="/ayush-product-list" style={S.ctaBtn}>Shop Now</Link>
                <Link to="/about" style={S.ctaBtnOutline}> About Us</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}